import { Static, Type } from '@sinclair/typebox';
import {
  AvailabilityBookabilityCode,
  AvailabilityBookabilitySeverity,
  AvailabilityBookabilityStatus,
} from './translation_namespace/availability_bookability_namespace';

/**
 * Réservabilité en ligne d'un planning de disponibilité — spécification arbitrée du
 * 25/08/2026 (règles H1-H12, évaluation statique). Champs purement DÉRIVÉS, calculés
 * par le backend à la lecture/sauvegarde : jamais persistés, jamais saisis.
 */

/**
 * Ordre de gravité des statuts — source UNIQUE du « le statut s'est-il dégradé ? »,
 * consommée par le diff backend (diffImpact) ET le diff client des fériés.
 */
export const AVAILABILITY_BOOKABILITY_STATUS_RANK: Record<AvailabilityBookabilityStatus, number> = {
  [AvailabilityBookabilityStatus.BOOKABLE]: 0,
  [AvailabilityBookabilityStatus.PARTIALLY_BOOKABLE]: 1,
  [AvailabilityBookabilityStatus.NOT_BOOKABLE]: 2,
};

export const AvailabilityBookabilityWarningSchema = Type.Object({
  code: Type.Enum(AvailabilityBookabilityCode),
  severity: Type.Enum(AvailabilityBookabilitySeverity),
  /** Cause temporaire (préavis/horizon/fériés) : se résout d'elle-même avec le temps. */
  is_temporary: Type.Optional(Type.Boolean()),
  /** Contexte d'interpolation ICU des messages (noms tronqués côté backend si besoin). */
  prestation_names: Type.Optional(Type.Array(Type.String())),
  place_labels: Type.Optional(Type.Array(Type.String())),
  duration_minutes: Type.Optional(Type.Number()),
  count: Type.Optional(Type.Number()),
});
export type AvailabilityBookabilityWarningObject = Static<typeof AvailabilityBookabilityWarningSchema>;

export const AvailabilityBookabilitySchema = Type.Object(
  {
    status: Type.Enum(AvailabilityBookabilityStatus),
    // ⚠️ transformToSchema convertit un tableau vide en null : Optional obligatoire,
    // le front lit toujours `?? []`.
    warnings: Type.Optional(Type.Array(AvailabilityBookabilityWarningSchema)),
  },
  // hidden : champ dérivé, ne doit jamais être rendu par DynamicForm.
  { meta: { hidden: true } },
);
export type AvailabilityBookabilityObject = Static<typeof AvailabilityBookabilitySchema>;

/**
 * Impact d'une mutation (suppression prestation/membre/lieu, paramètres de réservation,
 * ressources…) sur la réservabilité des plannings : diff AVANT/APRÈS ne comptant que les
 * DÉGRADATIONS (un planning déjà cassé ne re-déclenche pas d'alerte).
 */
export const AvailabilityBookabilityImpactSchema = Type.Object({
  newly_not_bookable_count: Type.Number(),
  newly_partially_bookable_count: Type.Number(),
  affected: Type.Optional(
    Type.Array(
      Type.Object({
        availability_id: Type.Number(),
        name: Type.String(),
        status: Type.Enum(AvailabilityBookabilityStatus),
      }),
    ),
  ),
});
export type AvailabilityBookabilityImpactObject = Static<typeof AvailabilityBookabilityImpactSchema>;

/** Réponse « message » enrichie de l'impact réservabilité (mutations delete). */
export const MessageWithBookabilityImpactResponseSchema = Type.Object({
  message: Type.String(),
  bookability_impact: Type.Optional(AvailabilityBookabilityImpactSchema),
});
export type MessageWithBookabilityImpactResponseObject = Static<typeof MessageWithBookabilityImpactResponseSchema>;
